// Filters for the vehicle label layers. Tested against the real MapLibre
// filter evaluator so a feature-shape change here cannot silently drop labels.
// `icon-text-fit-padding` is data-constant in the MapLibre style spec (zoom only,
// never per feature), so the compact ("tight") padding is a LAYER property: two
// badge layers, split by the feature's `labelTight` flag. Labels that overlap
// ("Stack as cards") are not badges at all: they carry a baked `labelCard` image
// and are drawn by the icon-only card layers, so every badge/plain filter skips them.
const flag = (key, on) => (on ? ['==', ['get', key], 1] : ['!=', ['get', key], 1]);
const NO_CARD = ['!', ['has', 'labelCard']];
const BADGE_BASE = [
  ['!', ['has', 'point_count']],
  ['==', ['get', 'kind'], 'vehicle'],
  ['has', 'labelShape'],
  ['!=', ['get', 'labelShape'], 'none'],
];
export const vehicleBadgeFilter = ({ tight = false } = {}) => ['all',
  ...BADGE_BASE,
  NO_CARD,
  flag('labelTight', tight),
];
export const VEHICLE_BADGE_LABEL_FILTER = vehicleBadgeFilter({ tight: false });

/** Vehicles whose label is a baked card ("Stack as cards"). */
export const VEHICLE_CARD_FILTER = ['all', ['!', ['has', 'point_count']], ['==', ['get', 'kind'], 'vehicle'], ['has', 'labelCard']];
/** `icon-text-fit-padding` [top, right, bottom, left] for the badge and its selection glow. */
export const BADGE_PADDING = [3, 8, 3, 8];
export const BADGE_PADDING_TIGHT = [1, 4, 1, 4];
export const GLOW_PADDING = [7, 12, 7, 12];
export const GLOW_PADDING_TIGHT = [4, 7, 4, 7];
/** The two badge layers, in draw order, with the layer-level props each one needs. */
export const BADGE_LAYER_VARIANTS = [
  { id: 'vehicle-label-badges', overlap: false, tight: false },
  { id: 'vehicle-label-badges-tight', overlap: false, tight: true },
].map((v) => ({ ...v, filter: vehicleBadgeFilter(v), padding: v.tight ? BADGE_PADDING_TIGHT : BADGE_PADDING }));
export const GLOW_LAYER_VARIANTS = [
  { id: 'vehicle-label-badge-glow', tight: false, padding: GLOW_PADDING },
  { id: 'vehicle-label-badge-glow-tight', tight: true, padding: GLOW_PADDING_TIGHT },
];
/** The SELECTED vehicle's badge: drawn by its own layers at the fixed `top` anchor
 *  (always shown, on top of the others) so it sits exactly over its glow. MapLibre keeps
 *  a spread label at whichever anchor it last used while that still fits, so a badge
 *  pushed aside in a crowd stays displaced after zooming in — a glow pinned to `top`
 *  would be left stranded under the marker (Hemn screenshots, 2026-10-31). */
export const SELECTED_BADGE_LAYER_VARIANTS = [
  { id: 'vehicle-label-badges-selected', tight: false, padding: BADGE_PADDING },
  { id: 'vehicle-label-badges-selected-tight', tight: true, padding: BADGE_PADDING_TIGHT },
];
/** Badge filter for one vehicle (the selection); sentinel id -1 matches nothing. */
export const vehicleBadgeSelectedFilter = (selectedId, tight = false) => ['all',
  ...BADGE_BASE,
  NO_CARD,
  flag('labelTight', tight),
  ['==', ['get', 'id'], selectedId ?? -1],
];
/** Badge filter for every vehicle but the selection (the spreading layers). */
export const vehicleBadgeUnselectedFilter = (selectedId, tight = false) => ['all',
  ...BADGE_BASE,
  NO_CARD,
  flag('labelTight', tight),
  ['!=', ['get', 'id'], selectedId ?? -1],
];

export const VEHICLE_PLAIN_LABEL_FILTER = ['all',
  ['!', ['has', 'point_count']],
  ['==', ['get', 'kind'], 'vehicle'],
  ['any', ['!', ['has', 'labelShape']], ['==', ['get', 'labelShape'], 'none']],
  NO_CARD,
];

/** Blue glow under the SELECTED vehicle's badge. Sentinel id -1 matches nothing.
 *  `tight` picks the compact-badge glow layer (smaller padding). A card gets no glow:
 *  the selected card image carries its own blue ring. */
export const vehicleBadgeGlowFilter = (selectedId, tight = false) => ['all',
  ...BADGE_BASE,
  NO_CARD,
  ['==', ['get', 'labelGlow'], 1],
  flag('labelTight', tight),
  ['==', ['get', 'id'], selectedId ?? -1],
];

// Per-feature minimum zoom + zoom-shrink. A layer's `minzoom` is global, and `['zoom']`
// may only be the input of a TOP-LEVEL step/interpolate, so every per-feature decision
// goes inside the stop outputs of one `step` over integer zooms: below the feature's
// `labelMinZoom` the size is 0 and the visibility multiplier is 0 (text + icon opacity).
export const LABEL_MIN_ZOOM_FLOOR = 6;
export const LABEL_MIN_ZOOM_CEIL = 16;
export const DEFAULT_LABEL_MIN_ZOOM = 12;

/** Badge text size (px) when "shrink when zoomed out" is on, by integer zoom. */
export function compactSizeAt(z) {
  if (z <= 8) return 7;
  if (z <= 10) return 8;
  if (z <= 12) return 9;
  if (z <= 14) return 10;
  return 11;
}
const minZoomOf = ['number', ['get', 'labelMinZoom'], DEFAULT_LABEL_MIN_ZOOM];
const hiddenAt = (z) => ['>', minZoomOf, z];
const zoomStep = (out) => {
  const e = ['step', ['zoom'], out(LABEL_MIN_ZOOM_FLOOR)];
  for (let z = LABEL_MIN_ZOOM_FLOOR + 1; z <= LABEL_MIN_ZOOM_CEIL; z += 1) e.push(z, out(z));
  return e;
};
/** Badge text: the feature's `labelTextSize` (px, default 11 = the original size;
 *  presets small/normal/large/xlarge → 9/11/14/17), one notch smaller when the badge
 *  is compact (`labelTight`); the zoom-shrink steps (`labelCompact`) are the base-11
 *  sizes scaled to the chosen size and never exceed the tight base. */
export const BADGE_TEXT_BASE = 11;
export const BADGE_TEXT_BASE_TIGHT = 10;
const textPx = ['number', ['get', 'labelTextSize'], BADGE_TEXT_BASE];
const tightPx = ['-', textPx, 1];
const shrinkPx = (z) => ['round', ['*', compactSizeAt(z), ['/', textPx, BADGE_TEXT_BASE]]];
const isTight = ['==', ['get', 'labelTight'], 1];
const isShrink = ['==', ['get', 'labelCompact'], 1];
export const BADGE_TEXT_SIZE = zoomStep((z) => ['case', hiddenAt(z), 0,
  ['case', isTight,
    ['case', isShrink, ['min', shrinkPx(z), tightPx], tightPx],
    ['case', isShrink, shrinkPx(z), textPx]]]);
/** Wrap width (ems): single-line labels (`labelOneLine`) must not wrap, two-line labels keep 14. */
export const BADGE_TEXT_MAX_WIDTH = ['case', ['==', ['get', 'labelOneLine'], 1], 60, 14];
export const PLAIN_TEXT_SIZE = zoomStep((z) => ['case', hiddenAt(z), 0, textPx]);
/** Card `icon-size`: a card is baked at its text size (one notch smaller when tight),
 *  so zoom-shrink scales the image by the same ratio BADGE_TEXT_SIZE applies to text;
 *  0 below the feature's minimum zoom. */
export const CARD_ICON_SIZE = zoomStep((z) => ['case', hiddenAt(z), 0,
  ['case', isShrink,
    ['case', isTight, ['/', ['min', shrinkPx(z), tightPx], tightPx], ['/', shrinkPx(z), textPx]],
    1]]);
/** The lowest `labelMinZoom` among vehicle features (default 12 when none carry one) —
 *  the label layers' `minzoom` follows it, so a fleet on the default pays no placement
 *  work below zoom 12 and only fleets that lower it lay out further out. */
export function lowestLabelMinZoom(features) {
  let min = DEFAULT_LABEL_MIN_ZOOM;
  for (const f of features || []) {
    const p = f?.properties;
    if (p?.kind === 'vehicle' && typeof p.labelMinZoom === 'number' && p.labelMinZoom < min) min = p.labelMinZoom;
  }
  return Math.max(LABEL_MIN_ZOOM_FLOOR, min);
}
export const CARD_LAYER_ID = 'vehicle-label-cards';
export const CARD_SELECTED_LAYER_ID = 'vehicle-label-cards-selected';
export const LABEL_LAYER_IDS = ['vehicle-labels', ...BADGE_LAYER_VARIANTS.map((v) => v.id), ...SELECTED_BADGE_LAYER_VARIANTS.map((v) => v.id), ...GLOW_LAYER_VARIANTS.map((v) => v.id), CARD_LAYER_ID, CARD_SELECTED_LAYER_ID];

/** 0/1 for text-opacity: hides a label below its own minimum zoom. */
export const LABEL_VISIBLE = zoomStep((z) => ['case', hiddenAt(z), 0, 1]);
/** `factor` (a number or a feature expression) above the feature's minimum zoom, 0 below it —
 *  the factor must sit INSIDE the step outputs; wrapping the step in `['*', …]` is invalid. */
export const labelVisibleTimes = (factor) => zoomStep((z) => ['case', hiddenAt(z), 0, factor]);

/** "Spread out" placement: MapLibre tries each anchor in turn (directly below the
 *  marker first, as today) and only hides a label when no side fits. With
 *  `icon-text-fit` the badge icon follows the anchor the text picked. */
export const SPREAD_ANCHORS = ['top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'];
/** Radial offsets (em) that keep today's downward distance for the `top` anchor. */
export const SPREAD_RADIAL_OFFSET_EM = 2.2;
export const SPREAD_RADIAL_OFFSET_PLAIN_EM = 1.8;
export function spreadLayout(radialEm) {
  return { 'text-variable-anchor': SPREAD_ANCHORS, 'text-radial-offset': radialEm, 'text-justify': 'auto' };
}

// Card `icon-offset` (image px, scaled by icon-size like the badge's em offset scales
// with its text): the card's top edge goes where the spread badge's top edge is. For
// a top anchor MapLibre subtracts the baseline offset (7/24 em) from the radial offset,
// so the badge top = (2.2 − 7/24) × text px − top padding (text one notch smaller and
// 1 px padding when compact). One literal per text-size preset (labelStyle TEXT_SIZE_PX).
const CARD_TEXT_PRESETS = [9, 11, 14, 17];
const BASELINE_OFFSET_EM = 7 / 24;
export function cardTopOffsetPx(textPx, tight = false) {
  const px = tight ? textPx - 1 : textPx;
  const padTop = (tight ? BADGE_PADDING_TIGHT : BADGE_PADDING)[0];
  return Math.round((SPREAD_RADIAL_OFFSET_EM - BASELINE_OFFSET_EM) * px - padTop);
}
const cardOffsetExpr = (delta) => {
  const byPreset = (tight) => ['match', ['number', ['get', 'labelTextSize'], BADGE_TEXT_BASE],
    ...CARD_TEXT_PRESETS.flatMap((px) => [px, ['literal', [0, cardTopOffsetPx(px, tight) + delta]]]),
    ['literal', [0, cardTopOffsetPx(BADGE_TEXT_BASE, tight) + delta]]];
  return ['case', isTight, byPreset(true), byPreset(false)];
};
export const CARD_ICON_OFFSET = cardOffsetExpr(0);
/** The selected card's ring adds 2 px per side, so it sits 2 px higher to keep the fills aligned. */
export const CARD_SELECTED_ICON_OFFSET = cardOffsetExpr(-2);
